import { useTranslation } from 'react-i18next';
import {
  SelectInput,
  BankCardInput,
  Checkbox,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from '@/shared/components';
import { createRegexInputHandlers, getRequiredLabel } from '@/shared/lib';
import type { CurrencyOption, CreateCardFormValues } from '../model';
import type { UseFormReturn } from 'react-hook-form';

interface CreateCardFormProps {
  form: UseFormReturn<CreateCardFormValues>;
  currencies: CurrencyOption[];
  cardIconConfig: Record<string, string>;
  initialValues?: Partial<CreateCardFormValues>;
}

export function CreateCardForm({
  form,
  currencies,
  cardIconConfig,
  initialValues,
}: CreateCardFormProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-[1.6rem]">
      <Form {...form}>
        <form
          className="flex flex-col gap-[1.6rem]"
          onSubmit={e => e.preventDefault()}>
          <FormField
            control={form.control}
            name="currencyCode"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <SelectInput
                    {...field}
                    disabled={Boolean(initialValues?.currencyCode)}
                    floatingLabel={getRequiredLabel(
                      t('withdrawMethods.crypto.assetLabel')
                    )}
                    options={currencies}
                    valueKey="code"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex flex-col gap-[0.4rem]">
            <FormField
              control={form.control}
              name="cardNumber"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <BankCardInput
                      {...field}
                      disabled={Boolean(initialValues?.cardNumber)}
                      cardIconConfig={cardIconConfig}
                      type="text"
                      className="p3"
                      showClearButton={false}
                      label={getRequiredLabel(
                        t('withdrawMethods.card.cardNumber')
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isCardholderCard"
              render={({ field }) => (
                <FormItem className="py-[0.8rem]">
                  <label className="flex flex-row items-center gap-[1.6rem] p4 cursor-pointer px-[1.6rem]">
                    <FormControl>
                      <Checkbox
                        className="cursor-pointer dark:aria-invalid:bg-light-error"
                        checked={Boolean(field.value)}
                        disabled={Boolean(initialValues?.isCardholderCard)}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div>{t('withdrawMethods.card.ownerCardConfirmation')}</div>
                  </label>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="cardholderName"
            render={({ field }) => {
              const handlers = createRegexInputHandlers(
                /^[A-Za-z ]$/,
                value => {
                  field.onChange(value.toUpperCase());
                }
              );

              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      type="text"
                      disabled={Boolean(initialValues?.cardholderName)}
                      className="p3"
                      label={getRequiredLabel(
                        t('withdrawMethods.card.cardHolderName')
                      )}
                      onBeforeInput={handlers.beforeInput}
                      onPaste={handlers.paste}
                      onChange={handlers.change}
                      onBlur={e => {
                        field.onChange(e.currentTarget.value.trim());
                        field.onBlur();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              );
            }}
          />

          <FormField
            control={form.control}
            name="templateName"
            render={({ field }) => {
              return (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      label={getRequiredLabel(
                        t('savedAddresses.card.fields.templateNameLabel')
                      )}
                      className="p3"
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage className="px-[1.6rem]" />
                </FormItem>
              );
            }}
          />
        </form>
      </Form>
    </div>
  );
}
